import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, MessageSquare, Calendar } from 'lucide-react';
import { businessInfo } from '../mockData';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    animalType: '',
    message: '',
    preferredDate: '',
    acceptTerms: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier que les conditions sont acceptées
    if (!formData.acceptTerms) {
      toast.error('Veuillez accepter les conditions générales');
      return;
    }
    
    try {
      // ENVOYER L'EMAIL EN PREMIER via Web3Forms
      const emailData = new FormData();
      emailData.append("access_key", "52808f2d-316d-42a7-9ffc-22eeb7397984");
      emailData.append("subject", `Nouvelle demande de RDV - ${formData.name}`);
      emailData.append("from_name", formData.name);
      emailData.append("email", formData.email);
      emailData.append("Telephone", formData.phone);
      emailData.append("Type_animal", formData.animalType === 'cheval' ? 'Cheval' : 'Chien');
      emailData.append("Date_souhaitee", formData.preferredDate || 'Non precisee');
      emailData.append("Message", formData.message || 'Aucun message');
      
      // Envoi email et attendre la réponse
      const emailResponse = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: emailData
      });
      const emailResult = await emailResponse.json();
      console.log('Email result:', emailResult);
      
      // Ensuite sauvegarder dans la base de données
      try {
        await axios.post(`${API}/appointments`, formData);
      } catch (dbError) {
        console.log('DB save failed but email sent:', dbError);
      }
      
      toast.success('Demande envoyée avec succès ! Je vous recontacterai rapidement.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        animalType: '',
        message: '',
        preferredDate: '',
        acceptTerms: false
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer ou me contacter par téléphone.');
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#8B9E8A] mb-4">
            Prendre Rendez-vous
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Contactez-moi pour offrir à votre animal un moment de bien-être
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h3 className="text-2xl font-bold text-[#8B9E8A] mb-6">
                Informations de Contact
              </h3>

              <div className="space-y-6 mb-8">
                <Card className="border-[#C5A572]/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#8B9E8A] p-3 rounded-full">
                        <Phone className="text-[#C5A572]" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#8B9E8A] mb-1">Téléphone</h4>
                        <a 
                          href={`tel:${businessInfo.phone}`}
                          className="text-[#C5A572] hover:text-[#8B9E8A] transition-colors font-medium"
                        >
                          {businessInfo.phone}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-[#C5A572]/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#8B9E8A] p-3 rounded-full">
                        <Mail className="text-[#C5A572]" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#8B9E8A] mb-1">Email</h4>
                        <a 
                          href={`mailto:${businessInfo.email}`}
                          className="text-[#C5A572] hover:text-[#8B9E8A] transition-colors font-medium"
                        >
                          {businessInfo.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-[#C5A572]/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#8B9E8A] p-3 rounded-full">
                        <MapPin className="text-[#C5A572]" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#8B9E8A] mb-1">Zone d'intervention</h4>
                        <p className="text-gray-600">{businessInfo.coverageArea}</p>
                        <p className="text-sm text-gray-500 mt-1">{businessInfo.location}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Contact Buttons */}
              <div className="space-y-3">
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center justify-center gap-3 w-full bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white px-6 py-4 rounded-full font-bold hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <Phone size={20} />
                  Appeler maintenant
                </a>
                <a
                  href={`sms:${businessInfo.phone}`}
                  className="flex items-center justify-center gap-3 w-full bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white px-6 py-4 rounded-full font-bold hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <MessageSquare size={20} />
                  Envoyer un SMS
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-[#C5A572]/30 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-[#8B9E8A] to-[#9CAF88] text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar />
                    Formulaire de Prise de Rendez-vous
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#8B9E8A] mb-2">
                        Votre nom *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-[#8B9E8A]/20 focus:border-[#C5A572] transition-colors"
                        placeholder="Nom complet"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#8B9E8A] mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="border-[#8B9E8A]/20 focus:border-[#C5A572] transition-colors"
                        placeholder="votre@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#8B9E8A] mb-2">
                        Téléphone *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="border-[#8B9E8A]/20 focus:border-[#C5A572] transition-colors"
                        placeholder="+33 X XX XX XX XX"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#8B9E8A] mb-2">
                        Type d'animal *
                      </label>
                      <select
                        name="animalType"
                        value={formData.animalType}
                        onChange={handleChange}
                        required
                        className="w-full border border-[#8B9E8A]/20 rounded-md px-3 py-2 focus:border-[#C5A572] focus:outline-none focus:ring-1 focus:ring-[#C5A572] transition-colors"
                      >
                        <option value="">Sélectionnez...</option>
                        <option value="cheval">🐴 Cheval</option>
                        <option value="chien">🐕 Chien</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#8B9E8A] mb-2">
                        Date souhaitée
                      </label>
                      <Input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        className="border-[#8B9E8A]/20 focus:border-[#C5A572] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#8B9E8A] mb-2">
                        Message / Informations complémentaires
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="border-[#8B9E8A]/20 focus:border-[#C5A572] transition-colors resize-none"
                        placeholder="Décrivez vos besoins, l'état de votre animal, etc."
                      />
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-[#C5A572]/20">
                      <Checkbox
                        id="acceptTerms"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, acceptTerms: checked }))}
                        className="mt-1"
                        required
                      />
                      <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer">
                        J'accepte les{' '}
                        <a
                          href="#terms"
                          onClick={(e) => {
                            e.preventDefault();
                            const element = document.getElementById('terms');
                            if (element) {
                              const offset = 80;
                              const elementPosition = element.getBoundingClientRect().top;
                              const offsetPosition = elementPosition + window.pageYOffset - offset;
                              window.scrollTo({
                                top: offsetPosition,
                                behavior: 'smooth'
                              });
                            }
                          }}
                          className="text-[#C5A572] hover:text-[#B8860B] font-semibold underline"
                        >
                          conditions générales
                        </a>{' '}
                        *
                      </label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white hover:shadow-xl transition-all duration-300 hover:scale-105 font-bold py-3 text-lg"
                    >
                      <Send className="mr-2" size={20} />
                      Envoyer ma demande
                    </Button>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      * Champs obligatoires - Je vous recontacterai dans les plus brefs délais
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
